import { ReactNode, useState } from 'react';
import {
  HighlightCard,
  HostNote,
  PlaceSheet,
  SectionDivider,
  StarRating,
  StopCard,
  Tab,
} from '../design-system';
import { getMapUrl, openInMaps, MAP_PROVIDERS } from '../../lib/maps';
import type { HandbookContent, ItineraryPlace } from '../../types';
import { getPlacePhotos } from '../../lib/placePhotos';
import lisbonUrl from '../../assets/illustrations/lisbon.svg?url';
import portoUrl from '../../assets/illustrations/porto.svg?url';
import { initialDayIndex, localISODate } from '../../lib/handbookDay';

/**
 * HandbookView — the traveler-facing handbook presentation (eyebrow, serif
 * title, host note, day pills, highlight + stop cards, place sheet).
 * Shared by the traveler HandbookPage and the host "Preview as traveler"
 * page so both always render the exact same thing.
 */

/** Free-form categories → canonical image-gradient palette keys. */
const CATEGORY_TO_GRADIENT: Record<string, string> = {
  food: 'food', restaurant: 'food', coffee: 'drink',
  drinks: 'drink', drink: 'drink', bar: 'drink',
  view: 'view', viewpoint: 'view',
  nature: 'nature', gardens: 'nature', park: 'nature',
  landmark: 'landmark', monument: 'landmark',
  art: 'art', museum: 'art', culture: 'art',
  shop: 'shop', shopping: 'shop', market: 'shop',
  travel: 'travel', transport: 'travel',
};

function gradientKeyFor(category: string): string {
  return CATEGORY_TO_GRADIENT[category?.toLowerCase()] ?? 'travel';
}

/** City name (free-form, e.g. "Porto" or "Lisbon, PT") → its hero illustration. */
function destinationIllustration(destination: string): string | null {
  const city = destination?.toLowerCase() ?? '';
  if (city.includes('lisbon')) return lisbonUrl;
  if (city.includes('porto')) return portoUrl;
  return null;
}

/** Map-provider chooser rendered in place of the sheet's primary button on native. */
function OpenInMapsAction({ place }: { place: ItineraryPlace }) {
  const [menuOpen, setMenuOpen] = useState(false);
  const [hover, setHover] = useState(false);
  const mapParams = { address: place.address, latitude: place.latitude, longitude: place.longitude, name: place.name };

  return (
    <div style={{ position: 'relative', marginBottom: 14 }}>
      <button
        type="button"
        onClick={() => {
          const result = openInMaps(mapParams);
          if (result === 'native') setMenuOpen((o) => !o);
        }}
        onMouseEnter={() => setHover(true)}
        onMouseLeave={() => setHover(false)}
        style={{
          width: '100%', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 8,
          fontFamily: 'var(--font-sans)', fontSize: 13, fontWeight: 600,
          background: 'var(--text)', color: 'var(--bg)', border: 'none', borderRadius: 999,
          padding: '15px 20px', cursor: 'pointer', transition: 'opacity .15s', opacity: hover ? 0.88 : 1,
        }}
      >
        <i className="ti ti-navigation" style={{ fontSize: 14 }} />Open in Maps
      </button>
      {menuOpen && (
        <div className="absolute bottom-full left-0 right-0 mb-2 bg-app-card border border-app-border rounded-xl shadow-2xl overflow-hidden z-10">
          {MAP_PROVIDERS.map((mp) => (
            <a
              key={mp.id}
              href={getMapUrl(mp.id, mapParams)}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center px-4 py-3 text-sm text-app-fg hover:bg-black/5 transition-colors"
              onClick={() => setMenuOpen(false)}
            >
              {mp.label}
            </a>
          ))}
        </div>
      )}
    </div>
  );
}

export interface HandbookViewProps {
  handbook: HandbookContent;
  /** Host first name — used in the note label and tip attribution. */
  hostFirstName: string;
  /** Host photo for the note card; falls back to a plain gradient disc when omitted. */
  hostAvatarUrl?: string | null;
  /** Rendered right of the title (approve button / approved badge). */
  headerRight?: ReactNode;
  /** Rendered above the eyebrow (back link / preview banner). */
  headerTop?: ReactNode;
  /** "Ask {host} about this" in the place sheet. Hidden when omitted. */
  onAskHost?: () => void;
  /** Open on today's day tab instead of the first (first day if the trip hasn't started). */
  openOnToday?: boolean;
  /** The traveler's own ratings by place id. `onRate(id, 0)` takes a rating back. */
  ratings?: Record<string, number>;
  /** Rate a place (traveler only). When omitted the stars show switched off (host / admin preview). */
  onRate?: (placeId: string, rating: number) => void;
}

export function HandbookView({ handbook, hostFirstName, hostAvatarUrl, headerRight, headerTop, onAskHost, openOnToday = false, ratings, onRate }: HandbookViewProps) {
  // Chosen once, when the handbook opens; tapping a tab afterwards is respected.
  const [activeDayIdx, setActiveDayIdx] = useState(() => (openOnToday ? initialDayIndex(handbook.days, localISODate()) : 0));
  const [selectedPlace, setSelectedPlace] = useState<ItineraryPlace | null>(null);

  const days = handbook.days;
  const activeDay = days[activeDayIdx];
  const dayLabels = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

  function formatDayPill(date: string, idx: number) {
    if (!date) return `Day ${idx + 1}`;
    const d = new Date(date + 'T12:00:00');
    return `${dayLabels[d.getDay()]}, ${d.toLocaleDateString('en', { month: 'short', day: 'numeric' })}`;
  }

  const highlightPlace = activeDay?.places.find((p) => p.highlight) ?? null;
  const moreStops = activeDay ? activeDay.places.filter((p) => p !== highlightPlace) : [];
  const heroIllustration = destinationIllustration(handbook.destination);

  return (
    <div className="px-5 pt-7 pb-20 md:px-10 md:pt-12 md:pb-[88px]">
      <div className="max-w-[800px] mx-auto">
        {headerTop}

        {/* Header */}
        {heroIllustration && (
          <div
            style={{
              width: 96, height: 96, marginBottom: 12,
              background: 'var(--text)',
              WebkitMask: `url(${heroIllustration}) no-repeat center / contain`,
              mask: `url(${heroIllustration}) no-repeat center / contain`,
            }}
          />
        )}
        <div className="text-app-muted text-xs font-medium mb-[10px]">Your {handbook.destination} handbook</div>
        <div className="flex items-start justify-between gap-5">
          <h1
            className="font-serif text-app-fg"
            style={{ fontSize: 40, fontWeight: 400, lineHeight: 1.06, letterSpacing: '-.015em', maxWidth: '16ch', textWrap: 'pretty' }}
          >
            {handbook.trip_name}
          </h1>
          {headerRight}
        </div>

        {/* Host note */}
        {handbook.note_for_traveler && (
          <HostNote
            hostName={`${hostFirstName}, your host`}
            quote={handbook.note_for_traveler}
            avatarUrl={hostAvatarUrl ?? undefined}
            style={{ marginTop: 32 }}
          />
        )}

        {/* Day pills */}
        <div className="flex gap-[7px] flex-wrap" style={{ marginTop: 36 }}>
          {days.map((day, i) => (
            <Tab key={i} active={activeDayIdx === i} onClick={() => setActiveDayIdx(i)}>
              {formatDayPill(day.date, i)}
            </Tab>
          ))}
        </div>

        {/* Highlight */}
        {highlightPlace && (
          <>
            <div className="flex items-center gap-2" style={{ marginTop: 44, marginBottom: 14 }}>
              <i className="ti ti-sparkles" style={{ color: 'var(--accent)', fontSize: 15 }} />
              <span className="font-serif text-app-fg" style={{ fontSize: 17 }}>Highlight</span>
            </div>
            <HighlightCard
              title={highlightPlace.name}
              tip={highlightPlace.tip ?? highlightPlace.description ?? ''}
              category={highlightPlace.category}
              gradientKey={gradientKeyFor(highlightPlace.category) as never}
              photos={getPlacePhotos(highlightPlace)}
              ownRating={ratings?.[highlightPlace.id]}
              onClick={() => setSelectedPlace(highlightPlace)}
            />
          </>
        )}

        {/* More to this day */}
        {moreStops.length > 0 && (
          <div style={{ marginTop: 44 }}>
            <SectionDivider
              icon="dots"
              label={highlightPlace ? 'More to this day' : 'This day'}
              style={{ marginBottom: 16 }}
            />
            <div className="flex flex-col" style={{ gap: 14 }}>
              {moreStops.map((place) => (
                <StopCard
                  key={place.id}
                  title={place.name}
                  tip={place.tip ?? place.description ?? ''}
                  category={place.category}
                  location={place.address ?? undefined}
                  gradientKey={gradientKeyFor(place.category) as never}
                  photoUrl={getPlacePhotos(place)[0]?.url}
                  photoFocal={getPlacePhotos(place)[0]?.focal}
                  ownRating={ratings?.[place.id]}
                  onClick={() => setSelectedPlace(place)}
                />
              ))}
            </div>
          </div>
        )}

        {activeDay && activeDay.places.length === 0 && (
          <p className="text-sm text-app-muted text-center py-8">No places planned for this day yet.</p>
        )}
      </div>

      {/* Place detail sheet */}
      {selectedPlace && (
        <PlaceSheet
          title={selectedPlace.name}
          category={selectedPlace.category}
          location={selectedPlace.address ? `${selectedPlace.address}` : handbook.destination}
          photos={getPlacePhotos(selectedPlace)}
          gradientKey={gradientKeyFor(selectedPlace.category)}
          hostName={hostFirstName}
          tip={selectedPlace.tip ?? undefined}
          primarySlot={
            (selectedPlace.address || (selectedPlace.latitude && selectedPlace.longitude))
              ? <OpenInMapsAction place={selectedPlace} />
              : null
          }
          secondaryLabel={onAskHost ? `Ask ${hostFirstName} about this` : undefined}
          onSecondary={onAskHost ? () => { setSelectedPlace(null); onAskHost(); } : undefined}
          onClose={() => setSelectedPlace(null)}
        >
          <div style={{ marginBottom: 20 }}>
            <div className="text-app-muted text-xs font-medium mb-1">
              {onRate ? (ratings?.[selectedPlace.id] ? 'Your rating' : 'Been here? Rate it') : 'Travelers rate places here'}
            </div>
            <StarRating
              value={ratings?.[selectedPlace.id] ?? 0}
              onChange={(n) => onRate?.(selectedPlace.id, n)}
              clearable
              disabled={!onRate}
            />
            {onRate && ratings?.[selectedPlace.id] ? (
              <button
                type="button"
                onClick={() => onRate(selectedPlace.id, 0)}
                className="mt-1 block text-xs text-app-muted underline"
              >
                Remove my rating
              </button>
            ) : null}
          </div>
          {selectedPlace.description && selectedPlace.description !== selectedPlace.tip && (
            <p className="text-[13px] text-app-fg-2 leading-relaxed mb-6" style={{ textWrap: 'pretty' }}>
              {selectedPlace.description}
            </p>
          )}
        </PlaceSheet>
      )}
    </div>
  );
}

export default HandbookView;
